<template>
  <div class="relative">
    <div
      class="flex items-center h-[100vh] inset-0 w-full bg-white bg-[radial-gradient(#e5e7eb_1.5px,transparent_1px)] [background-size:16px_16px]"
    >
      <div class="flex flex-col items-center w-full">
        <p class="font-bold font-inter text-4xl text-black">Navi Portal</p>
        <p class="font-inter text-slate-400 font-light mt-2 text-sm">
          Enter your credentials to login into your account
        </p>
        <div class="container mx-auto rounded-lg w-full max-w-xl md:p-6 mt-14">
          <form class="px-6" @submit.prevent="login">
            <div class="mb-5">
              <label
                for="email"
                class="block mb-2 text-sm font-medium text-gray-900 font-inter"
                >Email Address</label
              >
              <input
                type="email"
                id="email"
                v-model="email"
                class="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm block w-full py-2.5 px-4 rounded-full focus:border-orange-200"
                placeholder="e.g. johndoe@email.com"
                required
              />
            </div>
            <div class="mb-2">
              <label
                for="password"
                class="block mb-2 text-sm font-medium text-gray-90 font-inter"
                >Password</label
              >
              <input
                type="password"
                id="password"
                v-model="password"
                class="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm focus:border-orange-400 focus:ring-orange-400 block w-full py-2.5 px-4 rounded-full"
                placeholder="*******"
                required
              />
            </div>
            <div class="grid justify-items-end">
              <RouterLink to="/forgot-password">
                <p class="text-orange-400 hover:underline font-light text-sm">
                  Forgot Password?
                </p>
              </RouterLink>
            </div>
            <div class="grid justify-items-center mt-8 mb-5">
              <button
                v-if="!isSigningIn"
                type="submit"
                class="text-white bg-orange-400 font-medium text-sm px-5 py-2.5 text-center w-full rounded-full"
              >
                <span class="">Log In</span>
              </button>
              <div v-else role="status">
                <svg
                  aria-hidden="true"
                  class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-orange-300"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span class="sr-only">Loading...</span>
              </div>
            </div>
          </form>
        </div>
      </div>
      <!-- Login Alert -->
      <div
        v-if="showAlert"
        id="alert-2"
        class="absolute top-0 right-0 w-72 flex items-center p-4 mb-4 rounded-lg"
        :class="
          isSignInError
            ? 'text-red-800, bg-red-50'
            : 'text-green-800, bg-green-50'
        "
        role="alert"
      >
        <svg
          class="flex-shrink-0 w-4 h-4"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
          />
        </svg>
        <span class="sr-only">Info</span>
        <div class="ms-3 text-sm font-medium">{{ signInAlertMessage }}</div>
        <button
          type="button"
          :class="
            isSignInError
              ? 'bg-red-50 text-red-500 focus:ring-red-400 hover:bg-red-200'
              : 'bg-green-50 text-green-500 focus:ring-green-400 hover:bg-green-200'
          "
          class="ms-auto -mx-1.5 -my-1.5 rounded-lg focus:ring-2 p-1.5 inline-flex items-center justify-center h-8 w-8"
          data-dismiss-target="#alert-2"
          aria-label="Close"
        >
          <span class="sr-only">Close</span>
          <svg
          @click="showAlert = false"
            class="w-3 h-3"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 14"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import DataRepository from "@/infrastructure/services/DataRepository.js";
export default {
  data() {
    return {
      email: "",
      password: "",
      isSigningIn: false,
      signInAlertMessage: "",
      isSignInError: false,
      showAlert: false,
    };
  },
  methods: {
    async login() {
      //set sign in loading to true
      this.isSigningIn = true;
      const response = await DataRepository.loginUser(
        this.email,
        this.password
      );

      if (response.success && !response.error) {
        // Response is successful
        this.showAlert = true
        this.isSignInError = false
        this.signInAlertMessage = 'Login Successful'
        this.$router.push("/")
      }else{
        // Response failure
        this.showAlert = true
        this.isSignInError = true
        this.signInAlertMessage = `Failed to login! ${response.error}`
      }
      this.isSigningIn = false;
    },
  },
  watch: {
    "$store.state.loggedIn": async function () {
      if (this.$store.state.loggedIn) {
        this.$router.push(this.$store.state.redirectionUrl);
      }
    },
  },
};
</script>
